import { useMemo } from "react";
import useSWR from "swr";
import useIdentity from "@/features/ui/hooks/use-identity";
import useFetcher from "@/utils/use-fetcher";
import useListOrderSheetSets from "@/features/line-sheet-sets/hooks/use-list-order-sheet-sets";
import {
  getGetOrderSheetSetKey,
  GetOrderSheetSetRequest,
  GetOrderSheetSetResponse,
} from "@/features/line-sheet-sets/hooks/use-get-order-sheet-set";
import { toIdentityParameter } from "@/features/ui/helpers/identity-helpers";
import {
  LightOrderSheet,
  LightOrderSheetSet,
} from "@/features/order-sheets/order-sheet.type";

export interface LineSheetOrderSheetItem {
  orderSheetSet: LightOrderSheetSet;
  orderSheet: LightOrderSheet;
}

// 한 라인시트세트에 들어오는 오더는 많아야 수백 건이라 한 번에 받는다.
const PAGE_SIZE = 1000;

/**
 * 라인시트세트에 주문된 오더시트를 라인시트 id 별로 묶는다(최신 오더가 앞).
 *
 * 오더시트세트 목록 API는 오더시트(order_sheets)를 빈 배열로 내려준다(상세는 page_size=1 일 때만).
 * 그래서 목록으로 오더시트세트 id 만 받고, 각 오더시트세트 상세를 병렬로 불러 오더시트를 얻는다.
 *
 * - 임시저장 오더시트세트는 목록 API가 거른다(statusGroup ALL).
 * - 부티크는 목록 API가 자기에게 제출된(to_be_submitted) 오더만 내려준다.
 * - 세트를 제출해도 손대지 않은 라인시트의 오더시트는 수량 0으로 남는다 — 주문이 아니라 뺀다.
 */
export default function useLineSheetOrderSheetItems(
  lineSheetSetId: number | undefined,
  isEnabled: boolean
) {
  const identity = useIdentity();
  const identityParameter = toIdentityParameter(identity);

  const { data: listData, isLoading: isListLoading } = useListOrderSheetSets(
    isEnabled && lineSheetSetId ? identityParameter : null,
    {
      "lineSheetSet.id__in": lineSheetSetId ? [lineSheetSetId] : [],
      statusGroup__eq: "ALL",
      pageNumber: 1,
      pageSize: PAGE_SIZE,
    },
    { revalidateOnFocus: false }
  );

  const orderSheetSetIds = useMemo(
    () => listData?.orderSheetSets.map((set) => set.id) ?? [],
    [listData]
  );

  const detailFetcher = useFetcher<
    GetOrderSheetSetRequest,
    GetOrderSheetSetResponse
  >();

  const { data: details, isLoading: isDetailLoading } = useSWR(
    identityParameter && orderSheetSetIds.length > 0
      ? ["line-sheet-order-sheet-set-details", identityParameter, orderSheetSetIds]
      : null,
    async () => {
      const results = await Promise.allSettled(
        orderSheetSetIds.map((orderSheetSetId) =>
          detailFetcher(
            getGetOrderSheetSetKey(
              { ...identityParameter!, orderSheetSetId },
              {}
            )
          )
        )
      );
      // 한 건이 실패해도 나머지 오더는 보여준다.
      return results
        .filter(
          (r): r is PromiseFulfilledResult<GetOrderSheetSetResponse> =>
            r.status === "fulfilled"
        )
        .map((r) => r.value);
    },
    { revalidateOnFocus: false }
  );

  const itemsByLineSheetId = useMemo(() => {
    const map = new Map<number, LineSheetOrderSheetItem[]>();
    for (const orderSheetSet of details ?? []) {
      for (const orderSheet of orderSheetSet.orderSheets ?? []) {
        if (
          orderSheet.status === "INITIAL_DRAFT" ||
          orderSheet.orderedQuantity === 0
        ) {
          continue;
        }
        const items = map.get(orderSheet.lineSheet.id) ?? [];
        items.push({ orderSheetSet, orderSheet });
        map.set(orderSheet.lineSheet.id, items);
      }
    }
    map.forEach((items) =>
      items.sort(
        (a, b) =>
          toTime(b.orderSheetSet.submittedAt) -
          toTime(a.orderSheetSet.submittedAt)
      )
    );
    return map;
  }, [details]);

  return {
    itemsByLineSheetId,
    isLoading: isListLoading || isDetailLoading,
  };
}

function toTime(date: Date | string | null | undefined) {
  return date ? new Date(date).getTime() : 0;
}
